import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import PredictionCard from '../components/analysts/PredictionCard';
import PredictionFilters from '../components/analysts/PredictionFilters';
import AnalystLeaderboard from '../components/analysts/AnalystLeaderboard';
import AnalystSubmitForm from '../components/analysts/AnalystSubmitForm';
import SymbolGallery from '../components/analysts/SymbolGallery';
import SectorMapTab from '../components/analysts/SectorMapTab';
import { LayoutGrid, Trophy, BarChart3, SlidersHorizontal, Map } from 'lucide-react';
import ListLimitBanner from '../components/ui/ListLimitBanner';

const TABS = [
  { id: 'predictions', label: 'Predictions', icon: LayoutGrid },
  { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
  { id: 'symbols', label: 'Symbols', icon: BarChart3 },
  { id: 'sector-map', label: 'Sector Map', icon: Map },
];

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AnalystsHubPage() {
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get('tab') || 'predictions';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [predictions, setPredictions] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [loadingAnalysts, setLoadingAnalysts] = useState(false);
  const [predictionsListLimit, setPredictionsListLimit] = useState(null);
  const [analystsListLimit, setAnalystsListLimit] = useState(null);
  const [filters, setFilters] = useState({
    direction: '',
    platform: '',
    ordering: '-published_at',
  });

  // Leaderboard search + filter state
  const [analystSearch, setAnalystSearch] = useState('');
  const [analystAssetClass, setAnalystAssetClass] = useState('');
  const [analystCountry, setAnalystCountry] = useState('');
  const [analystOrdering, setAnalystOrdering] = useState('leaderboard_score');
  const [analystSector, setAnalystSector] = useState('');
  const [analystIndustry, setAnalystIndustry] = useState('');
  const [sectors, setSectors] = useState([]);
  const [industries, setIndustries] = useState([]);

  // Advanced threshold filters
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [minPredictions, setMinPredictions] = useState('');
  const [minReturnPct, setMinReturnPct] = useState('');
  // Staged inputs — only applied on "Apply"
  const [stagedMinPredictions, setStagedMinPredictions] = useState('');
  const [stagedMinReturnPct, setStagedMinReturnPct] = useState('');

  const activeAdvancedCount = [minPredictions, minReturnPct].filter(Boolean).length;

  useEffect(() => {
    if (activeTab !== 'predictions') return;
    const fetchPredictions = async () => {
      setLoadingPredictions(true);
      try {
        const params = new URLSearchParams();
        if (filters.direction) params.set('direction', filters.direction);
        if (filters.platform) params.set('channel__platform', filters.platform);
        if (filters.ordering) params.set('ordering', filters.ordering);
        const res = await axios.get(
          `${API_BASE_URL}/api/research/predictions/?${params}`,
          { headers: getAuthHeader() }
        );
        const data = res.data;
        const items = Array.isArray(data) ? data : data?.results ?? [];
        setPredictions(items);
        if (data?.list_limit_reached) {
          setPredictionsListLimit({ limit: data.list_limit, tier: data.list_limit_tier });
        } else {
          setPredictionsListLimit(null);
        }
      } catch (err) {
        console.error('Failed to load predictions', err);
        setPredictions([]);
        setPredictionsListLimit(null);
      } finally {
        setLoadingPredictions(false);
      }
    };
    fetchPredictions();
  }, [activeTab, filters]);

  useEffect(() => {
    if (activeTab !== 'leaderboard') return;
    const fetchAnalysts = async () => {
      setLoadingAnalysts(true);
      try {
        const params = new URLSearchParams();
        params.set('ordering', analystOrdering);
        if (analystSearch) params.set('search', analystSearch);
        if (analystAssetClass) params.set('asset_class', analystAssetClass);
        if (analystCountry) params.set('country', analystCountry);
        if (analystSector) params.set('sector', analystSector);
        if (analystIndustry) params.set('industry', analystIndustry);
        if (minPredictions) params.set('min_predictions', minPredictions);
        if (minReturnPct) params.set('min_return_pct', minReturnPct);
        const res = await axios.get(
          `${API_BASE_URL}/api/research/analysts/?${params}`,
          { headers: getAuthHeader() }
        );
        const data = res.data;
        const items = Array.isArray(data) ? data : data?.results ?? [];
        setAnalysts(items);
        if (data?.list_limit_reached) {
          setAnalystsListLimit({ limit: data.list_limit, tier: data.list_limit_tier });
        } else {
          setAnalystsListLimit(null);
        }
      } catch (err) {
        console.error('Failed to load analysts', err);
        setAnalysts([]);
        setAnalystsListLimit(null);
      } finally {
        setLoadingAnalysts(false);
      }
    };
    fetchAnalysts();
  }, [activeTab, analystSearch, analystAssetClass, analystCountry, analystOrdering, analystSector, analystIndustry, minPredictions, minReturnPct]);

  // Load sector list once when leaderboard tab is first opened
  useEffect(() => {
    if (activeTab !== 'leaderboard' || sectors.length > 0) return;
    axios.get(`${API_BASE_URL}/api/research/sectors/`)
      .then((res) => setSectors(res.data))
      .catch(() => {});
  }, [activeTab, sectors.length]);

  // Load industries when sector changes
  useEffect(() => {
    setAnalystIndustry('');
    if (!analystSector) { setIndustries([]); return; }
    axios.get(`${API_BASE_URL}/api/research/sectors/${encodeURIComponent(analystSector)}/industries/`)
      .then((res) => setIndustries(res.data))
      .catch(() => setIndustries([]));
  }, [analystSector]);

  function applyAdvancedFilters() {
    setMinPredictions(stagedMinPredictions);
    setMinReturnPct(stagedMinReturnPct);
  }

  function clearAdvancedFilters() {
    setStagedMinPredictions('');
    setStagedMinReturnPct('');
    setMinPredictions('');
    setMinReturnPct('');
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Analysts</h1>
        <p className="text-sm text-gray-500 mt-1">
          Track predictions from top market analysts and measure their performance.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1 w-fit mb-6">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === id
                ? 'bg-gray-900 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* Predictions tab */}
      {activeTab === 'predictions' && (
        <>
          <div className="mb-5">
            <PredictionFilters filters={filters} onChange={setFilters} />
          </div>

          {loadingPredictions ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse h-36" />
              ))}
            </div>
          ) : predictions.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <LayoutGrid size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No predictions found.</p>
              <p className="text-sm mt-1">Try adjusting the filters.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {predictions.map((pred) => (
                  <PredictionCard key={pred.id} prediction={pred} />
                ))}
              </div>
              {predictionsListLimit && (
                <ListLimitBanner limit={predictionsListLimit.limit} type="prediction" />
              )}
            </>
          )}
        </>
      )}

      {/* Symbols tab */}
      {activeTab === 'symbols' && <SymbolGallery />}

      {/* Sector Map tab */}
      {activeTab === 'sector-map' && (
        <SectorMapTab
          onSectorClick={({ label, level }) => {
            if (level === 'industry') {
              setAnalystIndustry(label);
            } else {
              setAnalystSector(label);
              setAnalystIndustry('');
            }
            setActiveTab('leaderboard');
          }}
        />
      )}

      {/* Leaderboard tab */}
      {activeTab === 'leaderboard' && (
        <>
          {/* Submit form — top of leaderboard */}
          <div className="mb-5 max-w-md">
            <AnalystSubmitForm />
          </div>

          {/* Search, filter, sort row */}
          <div className="flex flex-wrap gap-3 mb-5">
            {/* Search */}
            <input
              type="text"
              placeholder="Search analysts..."
              value={analystSearch}
              onChange={(e) => setAnalystSearch(e.target.value)}
              className="flex-1 min-w-[180px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />

            {/* Asset class filter chips */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {['', 'Equities', 'Crypto', 'Options', 'Commodities', 'Macro'].map((cls) => (
                <button
                  key={cls}
                  onClick={() => setAnalystAssetClass(cls)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    analystAssetClass === cls
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {cls || 'All'}
                </button>
              ))}
            </div>

            {/* Country filter */}
            <select
              value={analystCountry}
              onChange={(e) => setAnalystCountry(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">All Countries</option>
              <option value="US">US</option>
              <option value="Global">Global</option>
              <option value="BD">Bangladesh</option>
              <option value="IN">India</option>
              <option value="GB">UK</option>
            </select>

            {/* Sector filter */}
            <select
              value={analystSector}
              onChange={(e) => setAnalystSector(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">All Sectors</option>
              {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>

            {/* Industry filter (cascades from sector) */}
            {analystSector && (
              <select
                value={analystIndustry}
                onChange={(e) => setAnalystIndustry(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">All Industries</option>
                {industries.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            )}

            {/* Sort dropdown */}
            <select
              value={analystOrdering}
              onChange={(e) => setAnalystOrdering(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="-overall_return_pct">Returns (High)</option>
              <option value="-predictions_count">Most Predictions</option>
              <option value="-created_at">Newest</option>
              <option value="name">Name (A-Z)</option>
            </select>

            {/* Advanced toggle */}
            <button
              onClick={() => setShowAdvanced((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                activeAdvancedCount > 0
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              <SlidersHorizontal size={14} />
              Advanced
              {activeAdvancedCount > 0 && (
                <span className="ml-1 bg-white text-gray-900 text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {activeAdvancedCount}
                </span>
              )}
            </button>
          </div>

          {/* Advanced filter panel */}
          {showAdvanced && (
            <div className="flex flex-wrap items-end gap-4 mb-4 p-4 bg-white border border-gray-200 rounded-xl">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500">Min Predictions</label>
                <input
                  type="number"
                  min="0"
                  placeholder="e.g. 20"
                  value={stagedMinPredictions}
                  onChange={(e) => setStagedMinPredictions(e.target.value)}
                  className="w-28 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500">Min Return %</label>
                <input
                  type="number"
                  placeholder="e.g. 10"
                  value={stagedMinReturnPct}
                  onChange={(e) => setStagedMinReturnPct(e.target.value)}
                  className="w-28 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={applyAdvancedFilters}
                  className="px-4 py-1.5 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Apply
                </button>
                <button
                  onClick={clearAdvancedFilters}
                  className="px-4 py-1.5 text-sm font-medium bg-white text-gray-600 border border-gray-200 rounded-lg hover:border-gray-400 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Active filter summary bar */}
          {(analystAssetClass || analystCountry || analystSector || analystIndustry || activeAdvancedCount > 0) && (
            <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
              <span className="text-gray-400">Showing results for:</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {analystAssetClass && <span className="font-medium">{analystAssetClass}</span>}
                {analystCountry && <><span className="text-gray-300">·</span><span className="font-medium">{analystCountry}</span></>}
                {analystSector && <><span className="text-gray-300">·</span><span className="font-medium">{analystSector}</span></>}
                {analystIndustry && <><span className="text-gray-300">·</span><span className="font-medium">{analystIndustry}</span></>}
                {minPredictions && <><span className="text-gray-300">·</span><span className="font-medium">Min {minPredictions} predictions</span></>}
                {minReturnPct && <><span className="text-gray-300">·</span><span className="font-medium">Min {minReturnPct}% return</span></>}
              </div>
              <button
                onClick={() => {
                  setAnalystAssetClass('');
                  setAnalystCountry('');
                  setAnalystSector('');
                  setAnalystIndustry('');
                  clearAdvancedFilters();
                }}
                className="ml-auto text-xs text-gray-400 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          )}

          <AnalystLeaderboard analysts={analysts} loading={loadingAnalysts} />
          {analystsListLimit && (
            <ListLimitBanner limit={analystsListLimit.limit} type="analyst" />
          )}
        </>
      )}
    </div>
  );
}
